import { useInfiniteQuery, useMutation, useQuery } from "@tanstack/react-query";
import type { InfiniteData } from "@tanstack/react-query";
import {
  MarkAllNotificationsSeenResponseDto,
  NotificationDto,
  PagedNotificationsResponseDto,
} from "@repo/api/notification";
import { NotificationApi } from "../api/notification-api";
import { queryClient } from "./query-client";

const NOTIFICATIONS_PAGE_SIZE = 50;

type NotificationCursor = { createdAt: string; externalId: string } | undefined;

export function useNotificationsQuery({
  enabled = true,
}: { enabled?: boolean } = {}) {
  return useInfiniteQuery({
    queryKey: notificationQueryKeys.listNotifications(),
    queryFn: ({ pageParam }) =>
      NotificationApi.listNotificationsPaged({
        pageSize: NOTIFICATIONS_PAGE_SIZE,
        cursorCreatedAt: pageParam?.createdAt,
        cursorExternalId: pageParam?.externalId,
      }),
    getNextPageParam: (lastPage) => lastPage.nextCursor,
    initialPageParam: undefined as NotificationCursor,
    staleTime: 0,
    enabled,
    meta: {
      errorMessage: "Failed to fetch notifications",
    },
  });
}

export function useUnseenNotificationsQuery() {
  return useQuery({
    queryKey: notificationQueryKeys.unseenNotifications(),
    queryFn: () => NotificationApi.listUnseenNotifications(),
    refetchInterval: 30_000,
  });
}

export function useMarkNotificationSeenMutation({
  onSuccess,
  onError,
}: {
  onSuccess?: (data: NotificationDto) => void;
  onError?: (error: Error) => void;
} = {}) {
  return useMutation({
    mutationKey: notificationQueryKeys.markNotificationSeen(),
    mutationFn: (notificationExternalId: string) =>
      NotificationApi.markNotificationSeen(notificationExternalId),
    onSuccess: async (data) => {
      await queryClient.invalidateQueries({
        queryKey: notificationQueryKeys.all(),
      });
      onSuccess?.(data);
    },
    onError,
  });
}

export function useMarkAllNotificationsSeenMutation({
  onSuccess,
  onError,
}: {
  onSuccess?: (data: MarkAllNotificationsSeenResponseDto) => void;
  onError?: (error: Error) => void;
} = {}) {
  return useMutation({
    mutationKey: notificationQueryKeys.markAllNotificationsSeen(),
    mutationFn: () => NotificationApi.markAllNotificationsSeen(),
    onSuccess: async (data) => {
      await queryClient.invalidateQueries({
        queryKey: notificationQueryKeys.all(),
      });
      onSuccess?.(data);
    },
    onError,
  });
}

export function useDeleteNotificationMutation({
  onSuccess,
  onError,
}: {
  onSuccess?: (data: NotificationDto) => void;
  onError?: (error: Error) => void;
} = {}) {
  return useMutation({
    mutationKey: notificationQueryKeys.deleteNotification(),
    mutationFn: (notificationExternalId: string) =>
      NotificationApi.deleteNotification(notificationExternalId),
    onMutate: async (notificationExternalId) => {
      await queryClient.cancelQueries({
        queryKey: notificationQueryKeys.all(),
      });

      const previousList = queryClient.getQueryData<
        InfiniteData<PagedNotificationsResponseDto>
      >(notificationQueryKeys.listNotifications());
      const previousUnseen = queryClient.getQueryData<NotificationDto[]>(
        notificationQueryKeys.unseenNotifications(),
      );

      removeNotificationFromCache(notificationExternalId);

      return { previousList, previousUnseen };
    },
    onError: (error, _variables, context) => {
      if (context?.previousList) {
        queryClient.setQueryData(
          notificationQueryKeys.listNotifications(),
          context.previousList,
        );
      }
      if (context?.previousUnseen) {
        queryClient.setQueryData(
          notificationQueryKeys.unseenNotifications(),
          context.previousUnseen,
        );
      }
      onError?.(error);
    },
    onSuccess: async (data) => {
      await queryClient.invalidateQueries({
        queryKey: notificationQueryKeys.all(),
      });
      onSuccess?.(data);
    },
  });
}

export const notificationQueryKeys = {
  all: () => ["notification"],
  listNotifications: () => ["notification", "list"],
  unseenNotifications: () => ["notification", "unseen"],
  markNotificationSeen: () => ["notification", "mark-seen"],
  markAllNotificationsSeen: () => ["notification", "mark-all-seen"],
  deleteNotification: () => ["notification", "delete"],
};

export function removeNotificationFromCache(notificationExternalId: string) {
  queryClient.setQueryData<InfiniteData<PagedNotificationsResponseDto>>(
    notificationQueryKeys.listNotifications(),
    (existing) => {
      if (!existing) return existing;
      return {
        ...existing,
        pages: existing.pages.map((page) => ({
          ...page,
          items: page.items.filter(
            (item) => item.externalId !== notificationExternalId,
          ),
        })),
      };
    },
  );

  queryClient.setQueryData<NotificationDto[]>(
    notificationQueryKeys.unseenNotifications(),
    (existing) =>
      existing?.filter((n) => n.externalId !== notificationExternalId),
  );
}
